import axios from 'axios';
import type {
  AttachmentUploadResponse,
  AttachmentPage,
  AttachmentPreviewMeta,
  AttachmentApiError,
} from '../types/attachment';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

// ─── Axios instance ─────────────────────────────────────────────────────────

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

function getStoredToken(): string | null {
  return (
    localStorage.getItem('access_token') ??
    sessionStorage.getItem('access_token')
  );
}

// Request interceptor — attach Bearer token
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — unwrap ApiResponse<T> or extract error
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: (err?.['code'] as string) ?? `HTTP-${response.status}`,
        message: (err?.['message'] as string) ?? 'Unknown error',
        detail: (err?.['detail'] as string) ?? undefined,
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
        statusCode: response.status,
      } satisfies AttachmentApiError);
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const statusCode: number = error.response?.status ?? 0;
    const code = (errData?.['code'] as string) ?? `HTTP-${statusCode}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const detail = (errData?.['detail'] as string) ?? undefined;
    const traceId =
      (errData?.['traceId'] as string) ??
      (error.response?.headers?.['x-trace-id'] as string) ??
      undefined;
    const messageEn = (errData?.['messageEn'] as string) ?? undefined;
    const timestamp = (errData?.['timestamp'] as string) ?? undefined;

    return Promise.reject({
      code,
      message,
      detail,
      traceId,
      statusCode,
      messageEn,
      timestamp,
    } satisfies AttachmentApiError);
  }
);

// ─── API methods ─────────────────────────────────────────────────────────────

export const attachmentApi = {
  /**
   * POST /api/eraf/attachments
   * Uploads a file via multipart/form-data.
   * Reports upload progress via onProgress(0..100).
   */
  upload: (
    file: File,
    onProgress?: (pct: number) => void
  ): Promise<AttachmentUploadResponse> => {
    const form = new FormData();
    form.append('file', file);
    return apiClient
      .post<unknown, { data: AttachmentUploadResponse }>('/attachments', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (onProgress && evt.total) {
            onProgress(Math.round((evt.loaded * 100) / evt.total));
          }
        },
      })
      .then((res) => res.data);
  },

  /**
   * GET /api/eraf/attachments/me?page=0&size=20
   * Returns the current user's own attachment list (paginated).
   */
  listMine: (page = 0, size = 20): Promise<AttachmentPage> =>
    apiClient
      .get<unknown, { data: AttachmentPage }>('/attachments/me', {
        params: { page, size },
      })
      .then((res) => res.data),

  /**
   * DELETE /api/eraf/attachments/{id}
   */
  deleteOne: (fileId: string): Promise<void> =>
    apiClient
      .delete(`/attachments/${fileId}`)
      .then(() => undefined),

  /**
   * GET /api/eraf/attachments/{id}/preview
   * Returns preview metadata (kind, previewUrl).
   */
  getPreview: (fileId: string): Promise<AttachmentPreviewMeta> =>
    apiClient
      .get<unknown, { data: AttachmentPreviewMeta }>(`/attachments/${fileId}/preview`)
      .then((res) => res.data),

  /**
   * Downloads a file with proper Authorization header (JWT Bearer).
   * Uses fetch + blob + createObjectURL so the JWT is sent as a header
   * (not a query param) — required because the backend uses JwtAuthenticationFilter
   * which reads the token from Authorization header only.
   *
   * Falls back to a bare <a> navigation if no token is available (should not
   * happen in practice, but keeps behaviour graceful).
   */
  download: async (fileId: string, originalName: string): Promise<void> => {
    const base = import.meta.env.VITE_API_BASE ?? '/api/eraf';
    const url = `${base}/attachments/${fileId}`;
    const token = getStoredToken();

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw {
        code: `HTTP-${response.status}`,
        message: errorText || `Download failed: ${response.status}`,
        statusCode: response.status,
      };
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = originalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Release the object URL after a short delay to allow the download to start
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
  },
};
